import { useEffect, useState } from "react";
import "@/App.css";

const MENU_URL = "https://www.restaurantlogin.com/api/fb/bl_d_dg";
const BOOK_URL = "https://www.restaurantlogin.com/api/res/bl_d_dg";
const PHONE = "045 3247770";
const PHONE_TEL = "tel:+358453247770";
const ADDRESS = "Raviradantie 1, 50100 Mikkeli";
const MAPS_URL =
    "https://www.google.com/maps/search/?api=1&query=Raviradantie+1,+50100+Mikkeli";

const FB_URL = "https://www.facebook.com/ravintoula/?locale=fi_FI";
const IG_URL = "https://www.instagram.com/ressoravintola.mikkeli/";

const HERO_IMG =
    "https://images.unsplash.com/photo-1513104890138-7c749659a591?auto=format&fit=crop&w=1600&q=80";
const ABOUT_IMG =
    "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=1200&q=80";

const SPECIALTIES = [
    {
        icon: "fa-pizza-slice",
        title: "Pizzat",
        desc: "Tuoreesta taikinasta kiviuunissa paistettuja klassikoita ja erikoisuuksia.",
    },
    {
        icon: "fa-drumstick-bite",
        title: "Kebab",
        desc: "Mehevää, hitaasti grillattua kebabia rullissa, lautasella ja rapeissa pitoissa.",
    },
    {
        icon: "fa-burger",
        title: "Burgerit",
        desc: "Käsintehdyt burgerit, tuoreet sämpylät ja huolella valitut raaka-aineet.",
    },
    {
        icon: "fa-fire-flame-curved",
        title: "Grilliruoat",
        desc: "Grillin parhaita makuja – pihvit, siivet ja annokset kotiruoan rakkaudella.",
    },
];

const Nav = ({ scrolled }) => {
    const [open, setOpen] = useState(false);

    const links = [
        { label: "Etusivu", href: "#home" },
        { label: "Meistä", href: "#about" },
        { label: "Erikoisuudet", href: "#specialties" },
        { label: "Yhteystiedot", href: "#contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-[rgba(10,10,10,0.85)] backdrop-blur-md border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
                <a
                    href="#home"
                    data-testid="logo-link"
                    className="flex items-center gap-3 group"
                >
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-[var(--red)] text-white font-display text-2xl tracking-wider transition-transform group-hover:rotate-6">
                        R
                    </span>
                    <div className="leading-tight">
                        <div className="font-display text-2xl tracking-wider">
                            RESSO
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--muted)]">
                            Ravintola · Mikkeli
                        </div>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            className="nav-link"
                            data-testid={`nav-${l.href.slice(1)}`}
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href={BOOK_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="nav-book-btn"
                        className="btn-primary !py-3 !px-5 !text-xs"
                    >
                        Varaa pöytä
                    </a>
                </div>

                <button
                    aria-label="Avaa valikko"
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((o) => !o)}
                    className="md:hidden w-10 h-10 flex items-center justify-center text-[var(--cream)]"
                >
                    <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"} text-xl`}></i>
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden bg-[#0a0a0a] border-t border-white/5 px-6 py-6 space-y-5"
                >
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={() => setOpen(false)}
                            className="block text-lg font-medium tracking-wide"
                            data-testid={`mobile-nav-${l.href.slice(1)}`}
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href={BOOK_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary w-full justify-center mt-4"
                    >
                        Varaa pöytä
                    </a>
                </div>
            )}
        </header>
    );
};

const Hero = () => (
    <section
        id="home"
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden"
    >
        <div
            className="glow"
            style={{
                width: 500,
                height: 500,
                background: "var(--red)",
                opacity: 0.25,
                top: "10%",
                left: "-10%",
            }}
        />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
                <div className="fade-in delay-1">
                    <span className="eyebrow">Mikkeli · Est. 2024</span>
                </div>

                <h1 className="hero-title fade-in delay-2">
                    RESSO
                    <br />
                    <span className="accent">RAVINTOLA</span>
                </h1>

                <p className="fade-in delay-3 text-lg lg:text-xl text-[var(--muted)] max-w-xl leading-relaxed">
                    Pizzaa, kebabia, burgereita ja grillin parhaita makuja –
                    tuoreena valmistettuna Mikkelin sydämessä.
                </p>

                <div className="fade-in delay-4 flex flex-wrap gap-4">
                    <a
                        href={MENU_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-order-btn"
                        className="btn-primary"
                    >
                        <i className="fa-solid fa-bag-shopping"></i>
                        Tilaa verkosta
                    </a>
                    <a
                        href={BOOK_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-book-btn"
                        className="btn-outline"
                    >
                        <i className="fa-regular fa-calendar"></i>
                        Varaa pöytä
                    </a>
                </div>

                <div className="fade-in delay-4 pt-8 grid grid-cols-3 gap-6 max-w-md">
                    <div>
                        <div className="font-display text-4xl text-[var(--red)]">
                            100%
                        </div>
                        <div className="text-xs uppercase tracking-widest text-[var(--muted)] mt-1">
                            Tuoreena
                        </div>
                    </div>
                    <div>
                        <div className="font-display text-4xl text-[var(--red)]">
                            4
                        </div>
                        <div className="text-xs uppercase tracking-widest text-[var(--muted)] mt-1">
                            Erikoisuutta
                        </div>
                    </div>
                    <div>
                        <div className="font-display text-4xl text-[var(--red)]">
                            7
                        </div>
                        <div className="text-xs uppercase tracking-widest text-[var(--muted)] mt-1">
                            Päivää viikossa
                        </div>
                    </div>
                </div>
            </div>

            <div className="lg:col-span-5 fade-in delay-3">
                <div className="image-frame aspect-[4/5] border border-white/10">
                    <img
                        src={HERO_IMG}
                        alt="Resso ravintolan herkkuja"
                        loading="eager"
                    />
                </div>
                <div className="mt-4 flex items-center justify-between text-xs uppercase tracking-[0.25em] text-[var(--muted)]">
                    <span>— Aitoa makua</span>
                    <span>Mikkeli · FI</span>
                </div>
            </div>
        </div>
    </section>
);

const Marquee = () => (
    <div className="marquee" data-testid="marquee">
        <div className="marquee-track">
            {Array.from({ length: 2 }).flatMap((_, i) =>
                [
                    "Pizza",
                    "Kebab",
                    "Burger",
                    "Grilli",
                    "Tuoreena valmistettu",
                    "Mikkeli",
                ].map((w, j) => <span key={`${i}-${j}`}>{w}</span>),
            )}
        </div>
    </div>
);

const About = () => (
    <section
        id="about"
        data-testid="about-section"
        className="relative py-28 lg:py-36"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
                <div className="image-frame aspect-[4/5] border border-white/10 sticky top-32">
                    <img src={ABOUT_IMG} alt="Resso Ravintola Mikkeli" />
                </div>
            </div>

            <div className="lg:col-span-7 space-y-6">
                <span className="eyebrow">Meistä</span>
                <h2 className="section-title">
                    TERVETULOA RESSO
                    <br />
                    <span className="text-[var(--red)]">RAVINTOLAAN</span>
                </h2>

                <div className="divider-x my-6" />

                <div className="space-y-5 text-[var(--muted)] leading-relaxed text-[1.05rem]">
                    <p>
                        Resso Ravintola on paikka, jossa herkullinen ruoka,
                        laadukkaat raaka-aineet ja ystävällinen palvelu
                        kohtaavat. Olemme ylpeitä siitä, että tarjoamme
                        asiakkaillemme maukkaita pizzoja, meheviä burgereita,
                        herkullisia kebab-annoksia sekä grillin parhaita makuja
                        – aina tuoreena valmistettuna.
                    </p>
                    <p>
                        Meille ruoka on enemmän kuin vain ateria. Haluamme
                        tarjota jokaiselle asiakkaalle nautinnollisen
                        ruokailukokemuksen, olipa kyseessä nopea lounas, rento
                        illallinen ystävien kanssa tai herkullinen
                        takeaway-annos kotiin vietäväksi.
                    </p>
                    <p>
                        Valmistamme annoksemme huolella ja panostamme
                        tasalaatuiseen makuun jokaisella käyntikerralla.
                        Tavoitteenamme on olla paikka, johon asiakkaamme
                        palaavat yhä uudelleen hyvän ruoan, reilujen annosten
                        ja viihtyisän tunnelman vuoksi.
                    </p>
                    <p>
                        Resso Ravintola palvelee paikallisia asukkaita,
                        matkailijoita ja kaikkia hyvän ruoan ystäviä Mikkelin
                        sydämessä.
                    </p>
                    <p className="text-[var(--cream)] font-medium">
                        Lämpimästi tervetuloa nauttimaan suosikkimauistasi!
                    </p>
                </div>

                <div className="pt-4 border-l-2 border-[var(--red)] pl-5">
                    <p className="font-display text-2xl tracking-wide">
                        Resso Ravintola
                    </p>
                    <p className="text-sm text-[var(--muted)] mt-1">
                        Pizzaa, kebabia, burgereita ja grillin parhaita makuja.
                    </p>
                </div>
            </div>
        </div>
    </section>
);

const Specialties = () => (
    <section
        id="specialties"
        data-testid="specialties-section"
        className="relative py-28 lg:py-36 bg-[var(--bg-2)] border-y border-white/5"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
                <div className="lg:col-span-7">
                    <span className="eyebrow">Erikoisuudet</span>
                    <h2 className="section-title">
                        MAUT, JOITA
                        <br />
                        <span className="text-[var(--red)]">RAKASTAT</span>
                    </h2>
                </div>
                <div className="lg:col-span-5">
                    <p className="text-[var(--muted)] leading-relaxed">
                        Neljä intohimoa, yksi keittiö. Jokainen annos
                        valmistuu huolella käyttäen tuoreita raaka-aineita ja
                        rakkaudella.
                    </p>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {SPECIALTIES.map((s, i) => (
                    <div
                        key={s.title}
                        data-testid={`specialty-card-${i}`}
                        className="spec-card"
                    >
                        <i
                            className={`fa-solid ${s.icon} text-3xl text-[var(--red)]`}
                        ></i>
                        <h3 className="font-display text-3xl mt-6 tracking-wide">
                            {s.title}
                        </h3>
                        <p className="text-sm text-[var(--muted)] mt-3 leading-relaxed">
                            {s.desc}
                        </p>
                        <div className="mt-8 pt-5 border-t border-white/5 text-xs uppercase tracking-[0.25em] text-[var(--muted)] flex items-center justify-between">
                            <span>0{i + 1}</span>
                            <span>Tuoreena</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-16 flex flex-wrap gap-4 justify-center">
                <a
                    href={MENU_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="specialties-menu-btn"
                    className="btn-primary"
                >
                    <i className="fa-solid fa-bag-shopping"></i>
                    Katso koko ruokalista & tilaa
                </a>
                <a
                    href={BOOK_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="specialties-book-btn"
                    className="btn-outline"
                >
                    <i className="fa-regular fa-calendar"></i>
                    Varaa pöytä
                </a>
            </div>
        </div>
    </section>
);

const Contact = () => (
    <section
        id="contact"
        data-testid="contact-section"
        className="relative py-28 lg:py-36"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-2xl mx-auto mb-16">
                <span className="eyebrow justify-center inline-flex">
                    Yhteystiedot
                </span>
                <h2 className="section-title">
                    TULE <span className="text-[var(--red)]">KÄYMÄÄN</span>
                </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-5">
                <div className="spec-card text-center">
                    <i className="fa-solid fa-location-dot text-3xl text-[var(--red)]"></i>
                    <h3 className="font-display text-2xl mt-5 tracking-wider">
                        Osoite
                    </h3>
                    <p className="text-[var(--muted)] mt-3 leading-relaxed">
                        {ADDRESS}
                    </p>
                    <a
                        href={MAPS_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="contact-maps-link"
                        className="inline-flex items-center gap-2 mt-5 text-sm text-[var(--cream)] hover:text-[var(--red)] transition uppercase tracking-widest"
                    >
                        Avaa kartta <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                    </a>
                </div>

                <div className="spec-card text-center">
                    <i className="fa-solid fa-phone text-3xl text-[var(--red)]"></i>
                    <h3 className="font-display text-2xl mt-5 tracking-wider">
                        Puhelin
                    </h3>
                    <p className="text-[var(--muted)] mt-3 leading-relaxed">
                        Soita ja tilaa suoraan
                    </p>
                    <a
                        href={PHONE_TEL}
                        data-testid="contact-phone-link"
                        className="inline-flex items-center gap-2 mt-5 text-sm text-[var(--cream)] hover:text-[var(--red)] transition uppercase tracking-widest"
                    >
                        {PHONE} <i className="fa-solid fa-arrow-up-right-from-square text-xs"></i>
                    </a>
                </div>

                <div className="spec-card text-center">
                    <i className="fa-regular fa-clock text-3xl text-[var(--red)]"></i>
                    <h3 className="font-display text-2xl mt-5 tracking-wider">
                        Avoinna
                    </h3>
                    <div className="text-[var(--muted)] mt-3 leading-relaxed space-y-1">
                        <p>
                            <span className="text-[var(--cream)] font-medium">
                                Pe–La
                            </span>{" "}
                            11:00–23:00
                        </p>
                        <p>
                            <span className="text-[var(--cream)] font-medium">
                                Su–To
                            </span>{" "}
                            11:00–22:00
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-12 text-center">
                <a
                    href={BOOK_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="contact-book-btn"
                    className="btn-primary"
                >
                    <i className="fa-regular fa-calendar-check"></i>
                    Varaa pöytä
                </a>
            </div>
        </div>
    </section>
);

const Footer = () => (
    <footer
        data-testid="site-footer"
        className="border-t border-white/5 bg-[var(--bg-2)] pt-16 pb-8"
    >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-12">
            <div>
                <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-[var(--red)] text-white font-display text-2xl">
                        R
                    </span>
                    <div>
                        <div className="font-display text-2xl tracking-wider">
                            RESSO
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--muted)]">
                            Ravintola · Mikkeli
                        </div>
                    </div>
                </div>
                <p className="text-sm text-[var(--muted)] mt-5 leading-relaxed max-w-sm">
                    Pizzaa, kebabia, burgereita ja grillin parhaita makuja –
                    aina tuoreena valmistettuna Mikkelin sydämessä.
                </p>
                <div className="flex items-center gap-3 mt-6">
                    <a
                        href={FB_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="footer-facebook"
                        className="w-10 h-10 flex items-center justify-center border border-white/10 hover:bg-[var(--red)] hover:border-[var(--red)] transition"
                        aria-label="Facebook"
                    >
                        <i className="fa-brands fa-facebook-f"></i>
                    </a>
                    <a
                        href={IG_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="footer-instagram"
                        className="w-10 h-10 flex items-center justify-center border border-white/10 hover:bg-[var(--red)] hover:border-[var(--red)] transition"
                        aria-label="Instagram"
                    >
                        <i className="fa-brands fa-instagram"></i>
                    </a>
                </div>
            </div>

            <div>
                <h4 className="text-xs uppercase tracking-[0.3em] text-[var(--red)] mb-5">
                    Yhteys
                </h4>
                <ul className="space-y-3 text-sm">
                    <li className="flex gap-3 text-[var(--muted)]">
                        <i className="fa-solid fa-location-dot mt-1 text-[var(--red)]"></i>
                        <span>{ADDRESS}</span>
                    </li>
                    <li>
                        <a
                            href={PHONE_TEL}
                            className="flex gap-3 text-[var(--muted)] hover:text-[var(--cream)] transition"
                        >
                            <i className="fa-solid fa-phone mt-1 text-[var(--red)]"></i>
                            <span>{PHONE}</span>
                        </a>
                    </li>
                </ul>
            </div>

            <div>
                <h4 className="text-xs uppercase tracking-[0.3em] text-[var(--red)] mb-5">
                    Avoinna
                </h4>
                <ul className="space-y-3 text-sm text-[var(--muted)]">
                    <li className="flex justify-between border-b border-white/5 pb-2">
                        <span className="text-[var(--cream)]">Pe–La</span>
                        <span>11:00 – 23:00</span>
                    </li>
                    <li className="flex justify-between border-b border-white/5 pb-2">
                        <span className="text-[var(--cream)]">Su–To</span>
                        <span>11:00 – 22:00</span>
                    </li>
                </ul>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-14 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between gap-4 text-xs uppercase tracking-[0.25em] text-[var(--muted)]">
            <span>© {new Date().getFullYear()} Resso Ravintola. Kaikki oikeudet pidätetään.</span>
            <span>Mikkeli · Suomi</span>
        </div>
    </footer>
);

function App() {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <div className="App" data-testid="app-root">
            <Nav scrolled={scrolled} />
            <main>
                <Hero />
                <Marquee />
                <About />
                <Specialties />
                <Contact />
            </main>
            <Footer />
        </div>
    );
}

export default App;
